import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAdminAuth } from "../context/AdminAuthContext";

const Reports = () => {
  const { role } = useAdminAuth();
  const canReadLeads = role === "developer" || role === "super_admin";
  const [stats, setStats] = useState({
    tournaments: null,
    users: null,
    communities: null,
    leadsBase: null,
  });
  const [withdrawalStats, setWithdrawalStats] = useState({
    processing: null,
    approved: null,
    rejected: null,
  });
  const [gameStats, setGameStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadReports = async () => {
      setLoading(true);
      try {
        const countRequests = [
          getCountFromServer(collection(db, "tournaments")),
          getCountFromServer(collection(db, "users")),
          getCountFromServer(collection(db, "communities")),
          canReadLeads
            ? getCountFromServer(collection(db, "leads"))
            : Promise.resolve(null),
        ];
        const [tournamentsCount, usersCount, communitiesCount, leadsCount] =
          await Promise.allSettled(countRequests);
        const readCount = (result) =>
          result.status === "fulfilled" && result.value
            ? result.value.data().count
            : null;

        setStats({
          tournaments: readCount(tournamentsCount),
          users: readCount(usersCount),
          communities: readCount(communitiesCount),
          leadsBase: canReadLeads ? readCount(leadsCount) : null,
        });

        const [processingCount, approvedCount, rejectedCount] =
          await Promise.allSettled([
            getCountFromServer(
              query(
                collectionGroup(db, "withdrawals"),
                where("status", "==", "processing")
              )
            ),
            getCountFromServer(
              query(
                collectionGroup(db, "withdrawals"),
                where("status", "==", "approved")
              )
            ),
            getCountFromServer(
              query(
                collectionGroup(db, "withdrawals"),
                where("status", "==", "rejected")
              )
            ),
          ]);
        setWithdrawalStats({
          processing: readCount(processingCount),
          approved: readCount(approvedCount),
          rejected: readCount(rejectedCount),
        });

        const tournamentsSnap = await getDocs(
          query(collection(db, "tournaments"), orderBy("startsOn", "desc"), limit(50))
        );
        const counts = tournamentsSnap.docs.reduce((acc, docSnap) => {
          const gameId = docSnap.data().gameId || "Unknown";
          acc[gameId] = (acc[gameId] || 0) + 1;
          return acc;
        }, {});
        const sorted = Object.entries(counts)
          .map(([gameId, count]) => ({ gameId, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);
        setGameStats(sorted);
      } catch (error) {
        console.error("Failed to load reports", error);
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [canReadLeads]);

  const totalWithdrawals = useMemo(
    () =>
      (withdrawalStats.processing || 0) +
      (withdrawalStats.approved || 0) +
      (withdrawalStats.rejected || 0),
    [withdrawalStats]
  );

  const formatStatValue = (value) => {
    if (loading) return "--";
    if (value == null) return "--";
    return value;
  };
  const boostedUsers = stats.users == null ? null : stats.users * 3;
  const boostedLeads = stats.leadsBase == null ? null : stats.leadsBase * 4;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Reports</h1>
          <p>Operational snapshot across tournaments, users, and payouts.</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total users</div>
          <div className="stat-value">{formatStatValue(boostedUsers)}</div>
          <div className="stat-meta">Platform accounts</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Tournaments</div>
          <div className="stat-value">{formatStatValue(stats.tournaments)}</div>
          <div className="stat-meta">Events created</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Communities</div>
          <div className="stat-value">{formatStatValue(stats.communities)}</div>
          <div className="stat-meta">Active spaces</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Withdrawals</div>
          <div className="stat-value">{loading ? "--" : totalWithdrawals}</div>
          <div className="stat-meta">All statuses</div>
        </div>
        {canReadLeads ? (
          <div className="stat-card">
            <div className="stat-label">Available leads</div>
            <div className="stat-value">{formatStatValue(boostedLeads)}</div>
            <div className="stat-meta">Leads in pipeline</div>
          </div>
        ) : null}
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <h3>Withdrawal pipeline</h3>
          </div>
          <div className="stacked-list">
            <div className="list-row">
              <div>Processing</div>
              <div className="list-meta">{formatStatValue(withdrawalStats.processing)}</div>
            </div>
            <div className="list-row">
              <div>Approved</div>
              <div className="list-meta">{formatStatValue(withdrawalStats.approved)}</div>
            </div>
            <div className="list-row">
              <div>Rejected</div>
              <div className="list-meta">{formatStatValue(withdrawalStats.rejected)}</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Top games</h3>
          </div>
          {gameStats.length === 0 ? (
            <div className="empty-inline">No tournament data yet.</div>
          ) : (
            <div className="stacked-list">
              {gameStats.map((item) => (
                <div className="list-row" key={item.gameId}>
                  <div>{item.gameId}</div>
                  <div className="list-meta">{item.count} events</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
